import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { triggerNotification, useGetApiRequests } from '../../../common/CommonExport';
import Loader from '../../../components/Loader/Loader';
import { IspaDocumentProps } from './IspaDocumentTypes';
import { AxiosResponse, AxiosError } from 'axios';
import DownloadIcon from '../confidentialityLetter/DownloadIcon';
import { SelectedDownloadDocument, DocumentDetail, GetDocumentDataPayload, UploadSignaturePayload } from '../common/DocumentSignerTypes';
import { initializeSignatureUpload, initializeCheckbox, injectSignedImageAndHideForm, validateFormInputs } from './IspaDocumentHelper';

const IspaDocument = (props: IspaDocumentProps) => {
  const { getLetterDetails } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const [loader, setLoader] = useState(false);
  const imageBaseUrl = process.env.REACT_APP_IMAGE_VIEW;

  const uploadEmployeeSignatureIspa = useGetApiRequests('uploadEmployeeSignature', 'POST');
  const getDocumentDataIspa = useGetApiRequests("documentStatus", "GET");


  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const employeeId = searchParams.get('employeeId');
  const documentCode = searchParams.get('documentCode');
  const employeeApiKey = searchParams.get('employeeApiKey');

  const [selectedDownloadDocument, setSelectedDownloadDocument] = useState<SelectedDownloadDocument>({
    signed: null,
    unsigned: null,
    signature: null,
  });

  const targetFolderNames = [
    "information security privacy awareness agreement"
  ];

  useEffect(() => {
    if (containerRef.current && getLetterDetails) {
      containerRef.current.innerHTML = getLetterDetails;
      setTimeout(() => {
        initializeIspaLogic();
      }, 500);
      getDocumentStatus();
    }
  }, [getLetterDetails]);

  const initializeIspaLogic = () => {
    if (!containerRef.current) return;

    initializeSignatureUpload(
      containerRef.current,
      'employeeImageUpload',
      'employeeSignaturePreview',
      'employeeSignatureHandler',
      'employeeSignatureError'
    );

    initializeCheckbox(containerRef.current, 'training', 'trainingError');
    initializeCheckbox(containerRef.current, 'compliance', 'complianceError');
    initializeCheckbox(containerRef.current, 'responsibility', 'responsibilityError');

    const form = containerRef.current.querySelector('#signupdate');
    if (form) {
      form.addEventListener('submit', handleSubmit);
    }
  };

  const handleSubmit = (e: Event) => {
    e.preventDefault();
    if (!containerRef.current) return;

    const { isValid, employeeFile } = validateFormInputs(containerRef.current);

    if (isValid) {
      submitData(employeeFile);
    }
  };

  const submitData = (employeeFile: File | null) => {
    setLoader(true);
    const formData = new FormData();

    if (employeeFile) {
      formData.append("employeeSignature", employeeFile);
    }

    const params = {
      employeeId,
      documentCode,
      employeeApiKey
    };

    uploadEmployeeSignatureIspa(formData, {}, params)
      .then((res: AxiosResponse<UploadSignaturePayload> | AxiosError) => {
        setLoader(false);
        const response = res as AxiosResponse<UploadSignaturePayload>;

        if (response?.data?.status?.success?.toLowerCase() === "success") {
          triggerNotification('success', '', response.data.status.message, 'topRight');

          getDocumentStatus();

          const signaturePath = response.data.data?.signaturePath;

          if (signaturePath) {
            injectSignedImage(signaturePath);
          }
        } else {
          triggerNotification('error', '', response?.data?.status?.message || "Upload failed", 'topRight');
        }
      })
      .catch((err: AxiosError) => {
        setLoader(false);
        triggerNotification('error', '', err.message || "An error occurred", 'topRight');
      });
  };

  const getDocumentStatus = () => {
    const params = { employeeId, document: "documentDetail" };
    setLoader(true);

    getDocumentDataIspa("", {}, params)
      .then((res: AxiosResponse<GetDocumentDataPayload> | AxiosError) => {
        setLoader(false);
        const response = res as AxiosResponse<GetDocumentDataPayload>;

        response?.data?.data?.forEach((item: DocumentDetail) => {
          const folderName = item.document?.folderName?.toLowerCase();

          if (folderName && targetFolderNames.includes(folderName)) {

            if (item.name?.toLowerCase().includes('signed') && item.name?.toLowerCase().includes('pdf')) {
              setSelectedDownloadDocument((prev) => ({
                ...prev,
                signed: item,
              }));
              injectSignedImage(null);
            }
            if (item.documentPath && !item.documentPath.includes("pdf")) {
              injectSignedImage(item.documentPath);
            }
          }
        });
      })
      .catch(() => setLoader(false));
  };

  const injectSignedImage = (path: string | null) => {
    if (containerRef.current) {
      injectSignedImageAndHideForm(containerRef.current, path, imageBaseUrl);
    }
  };

  return (
    <div>
      {loader && <Loader />}
      <DownloadIcon selectedDownloadDocument={selectedDownloadDocument} />
      <div ref={containerRef} id="templateDetails" data-testid="ispa-template-container"></div>
    </div>
  );
};

export default IspaDocument;
